if (!customElements.get('before-after-slider')) {
  customElements.define(
    'before-after-slider',
    class BeforeAfterSlider extends HTMLElement {
      connectedCallback() {
        this.frame = this.querySelector('[data-frame]');
        this.handle = this.querySelector('[data-handle]');

        if (!this.frame || !this.handle) return;

        var initial = parseFloat(this.dataset.initialPosition);
        this._position = isNaN(initial) ? 50 : this.clamp(initial);
        this.isDragging = false;
        this.activePointerId = null;
        this.hasPulsed = false;

        // Bound handlers — kept on instance so they can be removed
        this.handlePointerDown = this.onPointerDown.bind(this);
        this.handlePointerMove = this.onPointerMove.bind(this);
        this.handlePointerUp = this.onPointerUp.bind(this);
        this.handleKeyDown = this.onKeyDown.bind(this);
        this.handleAnimationEnd = this.onAnimationEnd.bind(this);

        this.addEventListener('pointerdown', this.handlePointerDown);
        this.handle.addEventListener('keydown', this.handleKeyDown);
        this.addEventListener('animationend', this.handleAnimationEnd);

        this.applyPosition();
        this.setupIdlePulse();
      }

      disconnectedCallback() {
        this.removeEventListener('pointerdown', this.handlePointerDown);
        if (this.handle) {
          this.handle.removeEventListener('keydown', this.handleKeyDown);
        }
        this.removeEventListener('animationend', this.handleAnimationEnd);

        // In case a drag was in flight when the section was re-rendered
        document.removeEventListener('pointermove', this.handlePointerMove);
        document.removeEventListener('pointerup', this.handlePointerUp);
        document.removeEventListener('pointercancel', this.handlePointerUp);

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
          this.intersectionObserver = null;
        }
      }

      get position() {
        return this._position;
      }

      set position(value) {
        this._position = this.clamp(value);
        this.applyPosition();
      }

      clamp(value) {
        var n = Number(value);
        if (isNaN(n)) return 50;
        if (n < 0) return 0;
        if (n > 100) return 100;
        return n;
      }

      applyPosition() {
        this.style.setProperty('--position', this._position + '%');
        if (this.handle) {
          this.handle.setAttribute('aria-valuenow', Math.round(this._position));
        }
      }

      positionFromEvent(event) {
        var rect = this.frame.getBoundingClientRect();
        if (rect.width === 0) return this._position;
        var x = event.clientX - rect.left;
        return (x / rect.width) * 100;
      }

      onPointerDown(event) {
        if (event.button !== undefined && event.button !== 0) return;

        this.classList.remove('is-pulsing');

        this.isDragging = true;
        this.activePointerId = event.pointerId;
        this.classList.add('is-dragging');

        document.addEventListener('pointermove', this.handlePointerMove);
        document.addEventListener('pointerup', this.handlePointerUp);
        document.addEventListener('pointercancel', this.handlePointerUp);

        this.position = this.positionFromEvent(event);

        if (this.handle && event.target !== this.handle) {
          this.handle.focus({ preventScroll: true });
        }

        event.preventDefault();
      }

      onPointerMove(event) {
        if (!this.isDragging) return;
        if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;
        this.position = this.positionFromEvent(event);
      }

      onPointerUp(event) {
        if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;

        this.isDragging = false;
        this.activePointerId = null;
        this.classList.remove('is-dragging');

        document.removeEventListener('pointermove', this.handlePointerMove);
        document.removeEventListener('pointerup', this.handlePointerUp);
        document.removeEventListener('pointercancel', this.handlePointerUp);
      }

      onKeyDown(event) {
        var step = event.shiftKey ? 10 : 2;
        var handled = true;

        switch (event.key) {
          case 'ArrowLeft':
          case 'ArrowDown':
            this.position = this._position - step;
            break;
          case 'ArrowRight':
          case 'ArrowUp':
            this.position = this._position + step;
            break;
          case 'Home':
            this.position = 0;
            break;
          case 'End':
            this.position = 100;
            break;
          case 'PageDown':
            this.position = this._position - 10;
            break;
          case 'PageUp':
            this.position = this._position + 10;
            break;
          default:
            handled = false;
        }

        if (handled) {
          event.preventDefault();
          // Cancel idle pulse on first keyboard interaction too
          this.classList.remove('is-pulsing');
        }
      }

      setupIdlePulse() {
        if (this.hasPulsed) return;
        if (typeof IntersectionObserver === 'undefined') return;

        var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (motionQuery.matches) return;

        var self = this;
        this.intersectionObserver = new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting && !self.hasPulsed) {
                self.hasPulsed = true;
                self.classList.add('is-pulsing');
                if (self.intersectionObserver) {
                  self.intersectionObserver.disconnect();
                  self.intersectionObserver = null;
                }
                break;
              }
            }
          },
          { threshold: 0.5 }
        );

        this.intersectionObserver.observe(this);
      }

      onAnimationEnd(event) {
        if (event.animationName === 'before-after-pulse') {
          this.classList.remove('is-pulsing');
          // After the pulse keyframes finish, snap back to current value
          this.applyPosition();
        }
      }
    }
  );
}
